import { useEffect, useMemo, useState } from "react";
import { Button, Flex, Text, TextField } from "@radix-ui/themes";
import { Save } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import Modal from "@/components/ui/Modal";
import SearchableSelect, { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import ChartCreator from "@/components/entity/charts/ChartCreator";
import { useEntities } from "@/hooks/useCommonQueries";
import { chartConfigurationService } from "@/services/breeze/ChartConfigurationService";
import {
  ChartConfiguration,
  ChartDataSource,
  PlatformMetricCatalogEntry,
} from "@/types/breeze/ChartConfiguration";
import { PaginatedResult } from "@/types/breeze/Any";
import {
  CHART_TYPE_LABEL,
  chartNeedsXAxis,
  QUOTA_METRIC_OPTIONS,
  TIMEFRAME_LABEL,
} from "@/components/entity/charts/chartOptions";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (chart: ChartConfiguration) => void;
  existingChartId?: number | null;
}

interface SourceOption extends SearchableSelectOption {
  dataSource: ChartDataSource;
  metricId: string;
}

function buildInitialDraft(): ChartConfiguration {
  return {
    name: "",
    dataSource: "entity",
    chartType: "column",
    yAgg: "count",
    xInterval: "day",
    timeframePreset: "all",
    pieMode: "count",
  };
}

function draftToRequest(draft: ChartConfiguration) {
  return {
    name: draft.name,
    dataSource: draft.dataSource,
    chartType: draft.chartType,
    entityName: draft.entityName,
    xField: draft.xField,
    yField: draft.yField,
    pieMode: draft.pieMode,
    stackBy: draft.stackBy,
    yAgg: draft.yAgg,
    xInterval: draft.xInterval,
    timeframePreset: draft.timeframePreset,
    timeframeFrom: draft.timeframeFrom,
    timeframeTo: draft.timeframeTo,
    timeframeOffsetDays: draft.timeframeOffsetDays ?? null,
    compareEnabled: draft.compareEnabled ?? null,
    comparePreviousDays: draft.comparePreviousDays ?? null,
    compareShowOverlay: draft.compareShowOverlay ?? null,
    limit: draft.limit,
    sortDirection: draft.sortDirection,
    cumulative: draft.cumulative,
    target: draft.target,
    targetMode: draft.targetMode,
    filters: draft.filters ?? null,
    funnelStages: draft.funnelStages ?? null,
    funnelStyle: draft.funnelStyle ?? null,
    funnelMode: draft.funnelMode ?? null,
    cohortField: draft.cohortField ?? null,
    platformMetric: draft.platformMetric,
    platformPeriod: draft.platformPeriod,
    quotaMetric: draft.quotaMetric,
  };
}

function sourceOptionValue(source: SourceOption): string {
  return `${source.dataSource}:${source.metricId}`;
}

export default function ChartBuilderModal({ open, onOpenChange, onSaved, existingChartId }: Props) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ChartConfiguration>(() => buildInitialDraft());
  const [nameTouched, setNameTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = !!existingChartId;

  const { data: loadedChart } = useQuery({
    queryKey: ["chart-config", existingChartId],
    queryFn: () => chartConfigurationService.getChartConfiguration(existingChartId!),
    enabled: open && !!existingChartId,
    staleTime: 0,
  });

  useEffect(() => {
    if (!open || !loadedChart) return;
    setDraft({ ...loadedChart, timeframePreset: loadedChart.timeframePreset || "all" });
    setNameTouched(true);
  }, [open, loadedChart]);

  const patch = (partial: Partial<ChartConfiguration>) =>
    setDraft((d) => ({ ...d, ...partial }));

  const { data: platformCatalog = [] } = useQuery({
    queryKey: ["platform-metrics-catalog"],
    queryFn: () => chartConfigurationService.getPlatformMetrics(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: entities = [] } = useEntities();

  const sourceOptions: SourceOption[] = useMemo(() => {
    const entitySources: SourceOption[] = entities
      .filter((e) => !e.is_junction)
      .map((e) => ({
        value: `entity:${e.name}`,
        label: `${e.name} (Entity)`,
        dataSource: "entity",
        metricId: e.name,
      }));
    const platformSources: SourceOption[] = platformCatalog.map((m) => ({
      value: `platform:${m.id}`,
      label: `${m.label} (Platform)`,
      dataSource: "platform",
      metricId: m.id,
    }));
    const quotaSources: SourceOption[] = QUOTA_METRIC_OPTIONS.map((q) => ({
      value: `quota:${q.value}`,
      label: `${q.label} (Quota)`,
      dataSource: "quota",
      metricId: q.value,
    }));
    return [...entitySources, ...platformSources, ...quotaSources];
  }, [entities, platformCatalog]);

  const selectedSourceKey = draft.dataSource === "entity"
    ? (draft.entityName ? `entity:${draft.entityName}` : "")
    : draft.dataSource === "platform"
      ? (draft.platformMetric ? `platform:${draft.platformMetric}` : "")
      : draft.dataSource === "quota"
        ? (draft.quotaMetric ? `quota:${draft.quotaMetric}` : "")
        : "";

  // Keep in sync with backend QuotaToPlatformMetric — used so quota charts can borrow the platform metric's breakdown options and chart-type support.
  const QUOTA_TO_PLATFORM: Record<string, string> = {
    workflows: "workflow_runs",
    users: "users",
    files: "files",
  };

  const platformMetric: PlatformMetricCatalogEntry | null = useMemo(() => {
    if (draft.dataSource === "platform" && draft.platformMetric) {
      return platformCatalog.find((m) => m.id === draft.platformMetric) ?? null;
    }
    if (draft.dataSource === "quota" && draft.quotaMetric) {
      const platformId = QUOTA_TO_PLATFORM[draft.quotaMetric];
      if (platformId) return platformCatalog.find((m) => m.id === platformId) ?? null;
    }
    return null;
  }, [platformCatalog, draft.dataSource, draft.platformMetric, draft.quotaMetric]);

  useEffect(() => {
    // Quota borrows the platform metric's breakdown options — apply the same auto-select/validation logic.
    if (!platformMetric || (draft.dataSource !== "platform" && draft.dataSource !== "quota")) return;
    if (draft.stackBy && !platformMetric.stackByOptions.some((o) => o.id === draft.stackBy)) {
      patch({ stackBy: null });
    }
    if (draft.chartType === "pie" && !draft.stackBy && platformMetric.stackByOptions[0]) {
      patch({ stackBy: platformMetric.stackByOptions[0].id });
    }
  }, [platformMetric, draft.dataSource, draft.chartType, draft.stackBy]);

  const entityDefinition = useMemo(
    () => entities.find((e) => e.name === draft.entityName) ?? null,
    [entities, draft.entityName],
  );

  useEffect(() => {
    if (draft.dataSource !== "entity" || !entityDefinition || draft.xField) return;
    const hasCreatedAt = entityDefinition.fields.some((f) => f.name === "created_at");
    if (hasCreatedAt) patch({ xField: "created_at" });
  }, [draft.dataSource, draft.xField, entityDefinition]);

  const { data: entityPreviewData } = useQuery({
    queryKey: ["chart-builder-preview", draft.entityName],
    queryFn: () => chartConfigurationService.previewChartData(draft.entityName!),
    enabled: draft.dataSource === "entity" && !!draft.entityName,
    retry: false,
  });

  const entityData: PaginatedResult<Record<string, unknown>> | null = entityPreviewData
    ? (entityPreviewData as PaginatedResult<Record<string, unknown>>)
    : null;

  const defaultChartName = useMemo(() => {
    const option = sourceOptions.find((s) => sourceOptionValue(s) === selectedSourceKey);
    if (!option) return "";
    const bareLabel = option.label.replace(/ \(Entity\)| \(Platform\)| \(Quota\)/, "");
    const timeframe = draft.timeframePreset ? TIMEFRAME_LABEL[draft.timeframePreset] : null;
    const parts = [bareLabel, CHART_TYPE_LABEL[draft.chartType]];
    if (timeframe) parts.push(timeframe);
    return parts.join(" — ");
  }, [selectedSourceKey, sourceOptions, draft.chartType, draft.timeframePreset]);

  useEffect(() => {
    if (nameTouched) return;
    setDraft((d) => ({ ...d, name: defaultChartName }));
  }, [defaultChartName, nameTouched]);

  useEffect(() => {
    if (!open) {
      setDraft(buildInitialDraft());
      setNameTouched(false);
      setIsSaving(false);
    }
  }, [open]);

  const handleSourceChange = (value: string) => {
    const option = sourceOptions.find((s) => sourceOptionValue(s) === value);
    if (!option) return;
    const defaults: Partial<ChartConfiguration> =
      option.dataSource === "quota"
        ? { chartType: "gauge" }
        : option.dataSource === "platform"
          ? { chartType: "stat" }
          : { chartType: "column" };
    setDraft((d) => ({
      ...d,
      dataSource: option.dataSource,
      entityName: option.dataSource === "entity" ? option.metricId : null,
      platformMetric: option.dataSource === "platform" ? option.metricId : null,
      quotaMetric: option.dataSource === "quota" ? option.metricId : null,
      xField: null,
      yField: null,
      stackBy: null,
      funnelStages: null,
      funnelMode: null,
      funnelStyle: null,
      cohortField: null,
      ...defaults,
    }));
  };

  const handleSave = async () => {
    if (!draft.name.trim()) { toast.error("Give the chart a name"); return; }
    if (draft.dataSource === "entity" && !draft.entityName) { toast.error("Pick an entity"); return; }
    if (draft.dataSource === "platform" && !draft.platformMetric) { toast.error("Pick a platform metric"); return; }
    if (draft.dataSource === "quota" && !draft.quotaMetric) { toast.error("Pick a quota metric"); return; }
    if (draft.dataSource === "entity" && chartNeedsXAxis(draft.chartType) && !draft.xField) {
      toast.error("Pick an X-axis field"); return;
    }
    setIsSaving(true);
    try {
      const req = draftToRequest(draft);
      const chart = isEditing
        ? await chartConfigurationService.updateChartConfiguration(existingChartId!, req)
        : await chartConfigurationService.createChartConfiguration({ ...req, name: draft.name });
      await queryClient.invalidateQueries({ queryKey: ["charts-all-for-dashboard"] });
      toast.success(`Chart "${chart.name}" ${isEditing ? "updated" : "saved"}`);
      onSaved(chart);
      onOpenChange(false);
    } catch (err) {
      toast.error(`Failed to ${isEditing ? "update" : "save"} chart`);
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={isEditing ? "Edit chart" : "Design chart"}
      size="full"
      className="h-[90vh]"
    >
      <Flex direction="column" gap="4" p="2">
        <Flex gap="3" align="end" wrap="wrap">
          <Flex direction="column" gap="1" style={{ flex: "1 1 320px", minWidth: 260 }}>
            <Text as="div" size="2" weight="medium" color="gray"><label>Data source</label></Text>
            <SearchableSelect
              value={selectedSourceKey}
              onChange={handleSourceChange}
              options={sourceOptions}
              placeholder="Pick an entity, platform metric, or quota"
              searchPlaceholder="Search sources..."
              width={500}
            />
          </Flex>
          <Flex direction="column" gap="1" style={{ flex: "1 1 260px", minWidth: 220 }}>
            <Text as="div" size="2" weight="medium" color="gray"><label>Chart name</label></Text>
            <TextField.Root
              value={draft.name}
              onChange={(e) => { patch({ name: e.target.value }); setNameTouched(true); }}
              placeholder="Auto-named — edit to override"
            />
          </Flex>
          <Button
            size="2"
            disabled={!selectedSourceKey || !draft.name.trim() || isSaving}
            onClick={handleSave}
          >
            <Save className="w-4 h-4" />
            {isSaving ? (isEditing ? "Updating…" : "Saving…") : (isEditing ? "Update Chart" : "Save Chart")}
          </Button>
        </Flex>

        {!selectedSourceKey && (
          <Text size="2" color="gray">Pick a source to start designing a chart.</Text>
        )}

        {selectedSourceKey && (
          <ChartCreator
            draft={draft}
            entityDefinition={entityDefinition}
            entityData={entityData}
            platformMetric={platformMetric}
            onDraftChange={(next) => {
              setDraft(next);
              if (next.name !== draft.name) setNameTouched(true);
            }}
          />
        )}
      </Flex>
    </Modal>
  );
}
